import { useEffect, useMemo, useRef, useState } from 'react';
import { IconPlus, IconCheck, IconSearch } from '@tabler/icons-react';
import useTranslation from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { FilterableColumn } from '@/hooks/useTranscriptsFilters';

interface AddFilterPickerProps {
  columns: FilterableColumn[];
  activeColumnIds: Set<string>;
  onPick: (columnId: string) => void;
}

/**
 * Popover-based picker. Columns already filtered (an "active" column
 * is disabled + checkmarked so the user can't double-add).
 *
 * Keyboard model: arrow keys navigate, Enter picks, Escape closes via
 * Radix. Search is case-insensitive substring — adequate for the ~20
 * item typical list; no fuzzy dep needed.
 */
export function AddFilterPicker({ columns, activeColumnIds, onPick }: AddFilterPickerProps) {
  const { lang } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return columns;
    return columns.filter((c) => c.label.toLowerCase().includes(q));
  }, [columns, query]);

  // Reset picker state each time it opens.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setHighlighted(0);
  }, [open]);

  // Keep highlight within bounds as filter narrows.
  useEffect(() => {
    if (highlighted >= filtered.length) {
      setHighlighted(Math.max(0, filtered.length - 1));
    }
  }, [filtered.length, highlighted]);

  const pick = (columnId: string) => {
    if (activeColumnIds.has(columnId)) return;
    onPick(columnId);
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = filtered[highlighted];
      if (target) pick(target.id);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 px-3 text-xs gap-1.5">
          <IconPlus size={14} />
          {lang.get('msg.search') || 'Search'}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={4} className="w-64 p-0">
        <div className="flex items-center gap-2 border-b border-natural-700 px-2 py-1.5">
          <IconSearch size={14} className="text-gray-400 shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={lang.get('msg.searchColumns') || 'Search columns…'}
            className="w-full bg-transparent outline-none text-xs placeholder:text-gray-400"
            // autoFocus is fine here — Popover's own anim is already closed
            // by the time content mounts
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <div className="px-2 py-2 text-xs text-gray-500">
              {lang.get('msg.noColumns') || 'No matching columns'}
            </div>
          ) : (
            filtered.map((col, idx) => {
              const isActive = activeColumnIds.has(col.id);
              const isHighlighted = idx === highlighted;
              return (
                <button
                  key={col.id}
                  type="button"
                  disabled={isActive}
                  onClick={() => pick(col.id)}
                  onMouseEnter={() => setHighlighted(idx)}
                  className={[
                    'w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded text-xs text-left',
                    isActive
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-700 cursor-pointer',
                    !isActive && isHighlighted ? 'bg-gray-100' : '',
                  ].join(' ')}
                  title={col.label}
                >
                  <span className="truncate">{col.label}</span>
                  {isActive && <IconCheck size={14} className="shrink-0 text-gray-400" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
